import { useFragment } from "@/generated";
import { GetLessonByParentIdDocument, LessonFieldsFragmentDoc } from "@/generated/graphql";
import {
  type LessonsByParentOptions,
  getLessonsByParentIdVars,
} from "@/lib/utils/graphql/getLessonsByParentIdVars";
import { useQuery } from "@apollo/client/react";

/**
 * Fetches and paginates lessons belonging to a specific parent project.
 *
 * Queries the GraphQL API for lessons under a parent project with support for
 * infinite scrolling/pagination, search, and sorting. Results are cached (cache-first)
 * for performance. The loadMore function appends additional pages to the existing list.
 *
 * @param options - Configuration options for the hook
 * @param options.parentId - The unique identifier of the parent project
 * @param options.searchText - Optional search query to filter lessons by name
 * @param options.sortBy - Optional sort order (e.g., "ALPHABETICAL" or default to most recent)
 * @param options.skip - If true, skips the query execution (default: false)
 * @returns An object containing:
 *   - lessons: Array of Lesson objects for the current page(s)
 *   - hasMore: Boolean indicating if more lessons are available to load
 *   - loading: Boolean indicating if the initial query is in progress
 *   - loadMore: Function to fetch the next page of lessons
 *
 * @example
 * const { lessons, hasMore, loading, loadMore } = useLessonsByParent({
 *   parentId: "project123",
 *   searchText: "math"
 * });
 */
export function useLessonsByParent({
  parentId,
  searchText,
  sortBy,
  isTrash = false,
  skip = false,
}: LessonsByParentOptions) {
  const {
    data: lessonsByParentIdData,
    loading,
    fetchMore,
    error,
    refetch,
  } = useQuery(GetLessonByParentIdDocument, {
    variables: getLessonsByParentIdVars({
      parentId,
      searchText,
      sortBy,
      isTrash,
    }),
    skip,
    fetchPolicy: "cache-first",
  });

  const maskedItems = lessonsByParentIdData?.lessonsByParentId.items ?? [];
  const items = maskedItems
    .map((lesson) => useFragment(LessonFieldsFragmentDoc, lesson))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const hasMore = lessonsByParentIdData?.lessonsByParentId.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: {
        ...getLessonsByParentIdVars({
          parentId,
          searchText,
          sortBy,
          isTrash,
        }),
        offset: maskedItems.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const existingIds = new Set(
          useFragment(LessonFieldsFragmentDoc, prev.lessonsByParentId.items).map((l) => l.id)
        );
        const newItems = fetchMoreResult.lessonsByParentId.items.filter(
          (item) => !existingIds.has(useFragment(LessonFieldsFragmentDoc, item).id)
        );

        const unorderedItems = [...prev.lessonsByParentId.items, ...newItems];

        const orderedItems = useFragment(LessonFieldsFragmentDoc, unorderedItems).sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );

        return {
          lessonsByParentId: {
            ...fetchMoreResult.lessonsByParentId,
            items: orderedItems,
          },
        };
      },
    });
  };

  return {
    lessons: items,
    hasMore,
    loading,
    error,
    refetch,
    loadMore,
  };
}
